import React, { useState, useEffect } from 'react';
import { Shield, User, Check, X, Search, UserCog } from 'lucide-react';
import { fetchUsers } from '../services/usersApi';
import { updateUserPermissions } from '../services/burstModesApi';
import { usePermissions } from '../contexts/PermissionsContext';

const PERMISSION_CATEGORIES = {
  TEAMS: {
    name: 'Team Management',
    permissions: [
      { id: 'VIEW_TEAMS', name: 'View Teams', description: 'View teams and team members' },
      { id: 'MANAGE_TEAMS', name: 'Manage Teams', description: 'Create, edit, and delete teams' }
    ]
  },
  BURST_MODE: {
    name: 'Burst Modes',
    permissions: [
      { id: 'VIEW_BURST_MODES', name: 'View Burst Modes', description: 'View available burst modes' },
      { id: 'MANAGE_BURST_MODES', name: 'Manage Burst Modes', description: 'Create, edit, and delete burst modes' }
    ]
  },
  SETTINGS: {
    name: 'Settings',
    permissions: [
      { id: 'VIEW_SETTINGS', name: 'View Settings', description: 'Access settings panel' },
      { id: 'MANAGE_SETTINGS', name: 'Manage Settings', description: 'Modify application settings' }
    ]
  },
  ORGANIZATION: {
    name: 'Organization',
    permissions: [
      { id: 'VIEW_ORGANIZATION', name: 'View Organization', description: 'View organization settings' },
      { id: 'MANAGE_ORGANIZATION', name: 'Manage Organization', description: 'Manage organization settings' }
    ]
  }
};

const PermissionsSettings = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const { isAdmin } = usePermissions();

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await fetchUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setUserPermissions(user.appPermissions || []);
  };

  const togglePermission = (permissionId) => {
    setUserPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(p => p !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      await updateUserPermissions(selectedUser.id, userPermissions);
      
      // Update local state
      setUsers(users.map(u => 
        u.id === selectedUser.id 
          ? { ...u, appPermissions: userPermissions }
          : u
      ));
      
      alert('Permissions updated successfully');
    } catch (error) {
      console.error('Failed to update permissions:', error);
      alert('Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-4">User Permissions</h3>
        <div className="text-center py-8 text-gray-500">
          <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Admin access required</p>
          <p className="text-sm mt-1">Only administrators can manage user permissions</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="p-6 text-center">Loading users...</div>;
  }

  return (
    <div className="p-6">
      <h3 className="text-lg font-semibold mb-6">User Permissions</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* User List */}
        <div>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-claude-accent"
              />
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              {filteredUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => handleSelectUser(user)}
                  className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    selectedUser?.id === user.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-600" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-sm">{user.name || 'Unnamed User'}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user.isAdmin && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          Admin
                        </span>
                      )}
                      {user.appPermissions && user.appPermissions.length > 0 && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          {user.appPermissions.length} permissions
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Permission Editor */}
        <div>
          {selectedUser ? (
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium flex items-center gap-2">
                  <UserCog className="w-4 h-4" />
                  Permissions for {selectedUser.name || selectedUser.email}
                </h4>
                {selectedUser.isAdmin && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                    Admin (All Permissions)
                  </span>
                )}
              </div>

              {selectedUser.isAdmin ? (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">Administrators have all permissions by default</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(PERMISSION_CATEGORIES).map(([category, data]) => (
                    <div key={category}>
                      <h5 className="font-medium text-sm text-gray-700 mb-2">{data.name}</h5>
                      <div className="space-y-2">
                        {data.permissions.map(permission => (
                          <label
                            key={permission.id}
                            className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={userPermissions.includes(permission.id)}
                              onChange={() => togglePermission(permission.id)}
                              className="mt-0.5 rounded border-gray-300 text-claude-accent focus:ring-claude-accent"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{permission.name}</p>
                              <p className="text-xs text-gray-500">{permission.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="pt-4 border-t">
                    <button
                      onClick={handleSavePermissions}
                      disabled={saving}
                      className="w-full px-4 py-2 bg-claude-accent text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save Permissions'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-center py-12 text-gray-500">
                <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Select a user to manage permissions</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsSettings;